<template>
  <base-popup v-if="show" @close="$emit('close')">
    <template #header>
      <h3>Sélectionner une couleur</h3>
    </template>
    <template #bodyContent>
      <div class="color-picker-body">
        <div class="color-preview" :style="{ backgroundColor: rgbaColor }"></div>
        <div class="color-controls">
          <div class="color-gradient">
            <div class="gradient-preview" ref="gradientPreview" @mousedown="startGradientDrag">
              <div class="gradient-handle" :style="{ left: huePosition + '%' }"></div>
            </div>
          </div>
          <div class="color-square" ref="colorSquare" :style="{'--hue': hue}" @mousedown="startSquareDrag">
            <div class="square-handle" :style="{ left: saturationPosition + '%', top: brightnessPosition + '%' }"></div>
          </div>
          <div class="alpha-gradient" :style="{'background': `linear-gradient(to right, rgba(0, 0, 0, 0), ${rgbColor})`}" v-if="noAlpha == false">
            <div class="alpha-preview" ref="alphaPreview" @mousedown="startAlphaDrag">
              <div class="alpha-handle" :style="{ left: alpha + '%' }"></div>
            </div>
          </div>
          <div class="color-inputs">
            <div class="input-group">
              <label>Hex</label>
              <input type="text" v-model="hexColor" @input="updateFromHex" />
            </div>
            <div class="input-group">
              <label>RGBA</label>
              <input type="text" :value="rgbaColor" readonly />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="cancel-button" @click="$emit('close')">Annuler</button>
      <button class="validate-button" @click="validateColor">Valider</button>
    </template>
  </base-popup>
</template>

<script>
import BasePopup from './Base/BasePopup.vue'
export default {
  name: 'ColorPickerPopup',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    color: {
      type: String,
      default: 'rgba(255, 255, 255, 1)'
    },
    noAlpha: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BasePopup
  },
  data() {
    return {
      hue: 0,
      saturation: 100,
      brightness: 100,
      alpha: 100,
      hexColor: '#FFFFFF',
      isDraggingGradient: false,
      isDraggingSquare: false,
      isDraggingAlpha: false
    }
  },
  computed: {
    rgbColor() {
      const rgb = this.hsvToRgb(this.hue, this.saturation, this.brightness);
      return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    },
    rgbaColor() {
      const rgb = this.hsvToRgb(this.hue, this.saturation, this.brightness);
      if (this.noAlpha) {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
      }
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${this.alpha / 100})`;
    },
    huePosition() {
      return (this.hue / 360) * 100;
    },
    saturationPosition() {
      return this.saturation;
    },
    brightnessPosition() {
      return 100 - this.brightness;
    }
  },
  watch: {
    show: function() {
        if (this.show == true) {
            this.parseColor(this.color);
        }
    }
  },
  methods: {
    parseColor(color) {
      const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      if (rgba) {
        const rgb = {
          r: parseInt(rgba[1]),
          g: parseInt(rgba[2]),
          b: parseInt(rgba[3])
        };
        const hsv = this.rgbToHsv(rgb.r, rgb.g, rgb.b);
        this.hue = hsv.h;
        this.saturation = hsv.s;
        this.brightness = hsv.v;
        this.alpha = rgba[4] ? Math.round(parseFloat(rgba[4]) * 100) : 100;
        this.updateHex();
      }
    },
    startAlphaDrag(e) {
      this.isDraggingAlpha = true;
      this.updateAlphaPosition(e);
      document.addEventListener('mousemove', this.updateAlphaPosition);
      document.addEventListener('mouseup', this.stopAlphaDrag);
    },
    stopAlphaDrag() {
      this.isDraggingAlpha = false;
      document.removeEventListener('mousemove', this.updateAlphaPosition);
      document.removeEventListener('mouseup', this.stopAlphaDrag);
    },
    updateAlphaPosition(e) {
      if (!this.isDraggingAlpha) return;
      const rect = this.$refs.gradientPreview.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      this.alpha = Math.round(x * 100);
      this.updateHex();
    },
    startGradientDrag(e) {
      this.isDraggingGradient = true;
      this.updateGradientPosition(e);
      document.addEventListener('mousemove', this.updateGradientPosition);
      document.addEventListener('mouseup', this.stopGradientDrag);
    },
    stopGradientDrag() {
      this.isDraggingGradient = false;
      document.removeEventListener('mousemove', this.updateGradientPosition);
      document.removeEventListener('mouseup', this.stopGradientDrag);
    },
    updateGradientPosition(e) {
      if (!this.isDraggingGradient) return;
      const rect = this.$refs.gradientPreview.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      this.hue = Math.round(x * 360);
      this.updateHex();
    },
    startSquareDrag(e) {
      this.isDraggingSquare = true;
      this.updateSquarePosition(e);
      document.addEventListener('mousemove', this.updateSquarePosition);
      document.addEventListener('mouseup', this.stopSquareDrag);
    },
    stopSquareDrag() {
      this.isDraggingSquare = false;
      document.removeEventListener('mousemove', this.updateSquarePosition);
      document.removeEventListener('mouseup', this.stopSquareDrag);
    },
    updateSquarePosition(e) {
      if (!this.isDraggingSquare) return;
      const rect = this.$refs.colorSquare.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      this.saturation = Math.round(x * 100);
      this.brightness = Math.round((1 - y) * 100);
      this.updateHex();
    },
    updateHex() {
      const rgb = this.hsvToRgb(this.hue, this.saturation, this.brightness);
      const r = rgb.r.toString(16).padStart(2, '0');
      const g = rgb.g.toString(16).padStart(2, '0');
      const b = rgb.b.toString(16).padStart(2, '0');
      this.hexColor = `#${r}${g}${b}`;
    },
    updateFromHex() {
      const hex = this.hexColor.replace('#', '');
      if (hex.length >= 6) {
        const rgb = {
          r: parseInt(hex.substring(0, 2), 16),
          g: parseInt(hex.substring(2, 4), 16),
          b: parseInt(hex.substring(4, 6), 16)
        };
        const hsl = this.rgbToHsv(rgb.r, rgb.g, rgb.b);
        this.hue = hsl.h;
        this.saturation = hsl.s;
        this.brightness = hsl.v;
      }
    },
    hsvToRgb(h, s, v) {
         h /= 360;
         s /= 100;
         v /= 100;
        var r, g, b, i, f, p, q, t;
        if (arguments.length === 1) {
            s = h.s, v = h.v, h = h.h;
        }
        i = Math.floor(h * 6);
        f = h * 6 - i;
        p = v * (1 - s);
        q = v * (1 - f * s);
        t = v * (1 - (1 - f) * s);
        switch (i % 6) {
            case 0: r = v, g = t, b = p; break;
            case 1: r = q, g = v, b = p; break;
            case 2: r = p, g = v, b = t; break;
            case 3: r = p, g = q, b = v; break;
            case 4: r = t, g = p, b = v; break;
            case 5: r = v, g = p, b = q; break;
        }
        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    },
    rgbToHsv(r, g, b) {
        var max = Math.max(r, g, b), min = Math.min(r, g, b),
            d = max - min,
            h,
            s = (max === 0 ? 0 : d / max),
            v = max / 255;

        switch (max) {
            case min: h = 0; break;
            case r: h = (g - b) + d * (g < b ? 6: 0); h /= 6 * d; break;
            case g: h = (b - r) + d * 2; h /= 6 * d; break;
            case b: h = (r - g) + d * 4; h /= 6 * d; break;
        }
        return {
            h: Math.round(h * 360),
            s: Math.round(s * 100),
            v: Math.round(v * 100)
        };
    },
    validateColor() {
      this.$emit('update', this.rgbaColor);
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.color-picker-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.color-preview {
  width: 100%;
  height: 100px;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.color-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.color-gradient {
  position: relative;
  height: 20px;
  margin-bottom: 1rem;
  background: linear-gradient(to right, 
    #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000
  );
  border-radius: 10px;
  cursor: pointer;
}

.alpha-gradient {
  position: relative;
  height: 20px;
  margin-bottom: 1rem;
  border-radius: 10px;
  cursor: pointer;
}

.gradient-preview, .alpha-preview { 
    height: 100%;
}

.gradient-handle, .alpha-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background-color: white;
  border: 2px solid #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.color-square {
  position: relative;
  width: 100%;
  height: 200px;
  background: linear-gradient(to right, 
    #ffffff, 
    hsl(var(--hue, 0), 100%, 50%)
  );
  border-radius: 0.375rem;
  cursor: pointer;
  margin-bottom: 1rem;
}

.color-square::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, #000000, transparent);
  border-radius: 0.375rem;
}

.square-handle {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: white;
  border: 2px solid #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.color-inputs {
  display: flex;
  gap: 1rem;
}

.input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.input-group label {
  color: #e5e7eb;
  font-size: 0.875rem;
}

.input-group input {
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: #e5e7eb;
  font-size: 0.875rem;
}

.input-group input:focus {
  outline: none;
  border-color: #3b82f6;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.375rem;
  color: #e5e7eb;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.validate-button {
  padding: 0.5rem 1rem;
  background-color: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.375rem;
  color: #3b82f6;
  font-weight: 500;
  transition: all 0.2s ease;
}

.validate-button:hover {
  background-color: rgba(59, 130, 246, 0.3);
  border-color: #3b82f6;
}
</style> 